import {useEffect} from "react";
import {useNavigate} from "react-router-dom";

import {useGroup} from "~/contexts/group/GroupProvider";
import {useGroupSession} from "~/contexts/session/GroupSessionProvider";

/**
 * A hook for non-host clients in a co-op session.
 * It listens for changes to the `current_round` set by the host.
 * If the host starts a new level, this hook will automatically navigate
 * the client to a dedicated co-op waiting/playing screen.
 */
export const useCoopEnforcedRoute = () => {
  const navigate = useNavigate();
  const {isCoopMode} = useGroup();
  const {isHost, sessionData} = useGroupSession();

  useEffect(() => {
    // --- Guard Clauses ---
    // 1. This hook only applies to clients in a co-op session.
    // 2. It does nothing if there is no active round data.
    if (isHost || !isCoopMode || !sessionData?.current_round) {
      return;
    }

    // --- Core Logic ---
    const currentPath = window.location.pathname;
    const coopLobbyPath = "/coop"; // The designated path for co-op gameplay

    // If the host has started a round and we are not already on the co-op page, navigate there.
    if (currentPath !== coopLobbyPath) {
      navigate(coopLobbyPath);
    }
  }, [isCoopMode, isHost, sessionData?.current_round, navigate]);
  // The dependency on `sessionData.current_round` is key. This effect re-runs
  // every time the host starts a new round.
};
